import { ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface RequireAuthProps {
    children: ReactNode;
    roles?: string[]; // Optional role requirement (e.g., ['admin', 'super_admin'])
    redirectTo?: string; // Optional custom redirect path for unauthenticated users
}

export default function RequireAuth({ children, roles, redirectTo = '/auth/sign-in' }: RequireAuthProps) {
    const { user, tenants, roles: userRoles, isLoading } = useAuth();
    const location = useLocation();
    const { tenantId: tenantSlug } = useParams<{ tenantId?: string }>();

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
                    <p className="text-lg text-gray-700">Loading...</p>
                </div>
            </div>
        );
    }

    // Not authenticated - redirect to sign in (or custom redirect path)
    if (!user) {
        // Preserve the current location to redirect back after login
        return <Navigate to={redirectTo} state={{ from: location }} replace />;
    }

    const isSuperAdmin = userRoles.includes('super_admin');

    // Check tenant requirement FIRST - users without tenants should complete onboarding
    // before we check role requirements (creating a tenant grants admin role)
    if ((!tenants || tenants.length === 0) && !isSuperAdmin && location.pathname !== '/onboarding') {
        return <Navigate to="/onboarding" state={{ from: location }} replace />;
    }

    // Check role requirements (only after tenant check passes)
    if (roles && roles.length > 0) {
        // super_admin can access everything
        if (!isSuperAdmin) {
            // Tenant-scoped admin routes should be authorized via tenant membership (tenant_members).
            // Global roles (app_roles/user_roles) are not sufficient to grant access to an arbitrary tenant.
            if (tenantSlug) {
                const tenant = tenants?.find(t => t.slug === tenantSlug || t.id === tenantSlug);
                const tenantRole = tenant?.role;
                const isTenantAdmin = tenant?.is_owner || tenantRole === 'owner' || tenantRole === 'admin';
                // Fallback: if user has a global admin role AND is a member of this tenant, allow access.
                const hasGlobalAdmin = userRoles.includes('admin') || userRoles.includes('super_admin');
                const isTenantMember = Boolean(tenant);

                if (!isTenantAdmin && !(hasGlobalAdmin && isTenantMember)) {
                    return (
                        <div className="min-h-screen flex items-center justify-center bg-gray-50">
                            <div className="max-w-md mx-auto text-center p-8">
                                <h1 className="font-serif text-3xl mb-4 text-gray-900">Access Denied</h1>
                                <p className="text-lg text-gray-600 mb-8">
                                    You don&apos;t have permission to access this tenant. Tenant admin access is required.
                                </p>
                                <a
                                    href="/onboarding"
                                    className="inline-block bg-pink-600 text-white px-8 py-3 rounded-lg hover:opacity-90 transition-opacity"
                                >
                                    Go to Onboarding
                                </a>
                            </div>
                        </div>
                    );
                }
            } else {
                // Non-tenant routes can rely on global roles.
                const hasRequiredRole = roles.some(r => userRoles.includes(r));
                if (!hasRequiredRole) {
                    return (
                        <div className="min-h-screen flex items-center justify-center bg-gray-50">
                            <div className="max-w-md mx-auto text-center p-8">
                                <h1 className="font-serif text-3xl mb-4 text-gray-900">Access Denied</h1>
                                <p className="text-lg text-gray-600 mb-8">
                                    You don&apos;t have permission to access this page. This area is restricted.
                                </p>
                                <a
                                    href="/onboarding"
                                    className="inline-block bg-pink-600 text-white px-8 py-3 rounded-lg hover:opacity-90 transition-opacity"
                                >
                                    Go to Onboarding
                                </a>
                            </div>
                        </div>
                    );
                }
            }
        }
    }

    // Authenticated and authorized
    return <>{children}</>;
}
